import {defineMessages, FormattedMessage} from 'react-intl';

import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import moment from 'moment';

import {DEFAULT_PARENT_ID} from '@components/Organizations/constants';
import {airlineRatingChangesMessages} from '@components/Preferences/AirlineRatingChangesPreferenceModal/constants';

import {inversePersonLocationTypeMap, NOTIFICATION_TYPES} from '@redux/modules/preferences/utils';

import {ALERT_SEVERITIES} from '@constants/alert-severity-keys';
import {ALL_CATEGORIES_GUID} from '@constants/categories';
import {AIRLINE_RATINGS} from '@constants/world-airlines';

import localization from '../services/localization';

const messages = defineMessages({
  allCategoriesLabel: {
    id: 'preferences.allCategoriesLabel',
    defaultMessage: 'All',
  },
  SMS: {
    id: 'preferences.SMS',
    defaultMessage: 'SMS',
  },
  EMAIL: {
    id: 'preferences.EMAIL',
    defaultMessage: 'Email',
  },
  PUSH: {
    id: 'preferences.PUSH',
    defaultMessage: 'Push',
  },
  HIGHLY_PREFERRED: {
    id: 'preferences.HIGHLY_PREFERRED',
    defaultMessage: 'Highly Preferred',
  },
  MODERATELY_PREFERRED: {
    id: 'preferences.MODERATELY_PREFERRED',
    defaultMessage: 'Moderately Preferred',
  },
  NOT_PREFERRED: {
    id: 'preferences.NOT_PREFERRED',
    defaultMessage: 'Not Preferred',
  },
  NOT_RATED: {
    id: 'preferences.NOT_RATED',
    defaultMessage: 'Not Rated',
  },
  required: {
    id: 'preferences.NotificationTriggerSelector.required',
    defaultMessage: 'This input is required',
  },
});

export function formatCategories(categories, alertCategoriesDict) {
  return categories?.map((categoryId) => {
    if (categoryId === ALL_CATEGORIES_GUID) {
      return {
        id: categoryId,
        name: <FormattedMessage {...messages.allCategoriesLabel} />,
        value: categoryId,
      };
    }
    return alertCategoriesDict[categoryId] || {id: categoryId, name: categoryId, value: categoryId};
  });
}

export function formatSeverities(severities) {
  return severities?.map((severity) => ({
    name: <FormattedMessage {...ALERT_SEVERITIES[severity.toUpperCase()].message} />,
    id: ALERT_SEVERITIES[severity.toUpperCase()].severityValue,
  }));
}

export function formatModalities(modalities) {
  return modalities.map((modality) => {
    return {
      id: modality.type,
      name: <FormattedMessage {...messages[modality.type]} />,
    };
  });
}

export function formatLocations(locations, locationsDict) {
  return locations?.map((locationId) => {
    const loc = locationsDict[locationId];
    if (!loc) {
      return {};
    }
    return {
      id: loc.value,
      name: loc.name,
      title: loc.name,
      label: loc.name,
      value: loc.value,
    };
  });
}

export const hasValues = (details) => values(details).reduce((accumulator, value) => accumulator || !!value, false);

export const NOTIFICATION_TRIGGERS = {
  AIRLINE_PREFERENCE_RATINGS: 'airlinePreferenceRatings',
  AIRLINES: 'airlines',
};

export const NOTIFICATION_TRIGGER_SELECTOR_FIELD_NAMES = {
  NOTIFICATION_SELECTOR: 'notificationSelector',
  AIRLINE_PREFERENCE_RATING: 'airlinePreferenceRatings',
  LOCATION_IDS: 'locationIds',
  AIRLINE_IDS: 'airlineIds',
};

export const getAirlinePreferenceRatingsOptions = () => {
  return Object.keys(AIRLINE_RATINGS).map((key) => {
    const message = messages[key];
    return {
      value: key,
      label: <FormattedMessage {...message} />,
    };
  });
};

export const getMaybeRequiredValidator =
  (notificationTriggerType, siblings = []) =>
  ({getFieldValue}) => ({
    validator(_field, value) {
      const fieldValue = getFieldValue(NOTIFICATION_TRIGGER_SELECTOR_FIELD_NAMES.NOTIFICATION_SELECTOR);
      const isNotMatchingTrigger = fieldValue !== notificationTriggerType;
      const hasValue = value && !isEmpty(value);

      const atLeastOneSiblingWithValue = !isEmpty(siblings)
        ? siblings
            .map((siblingName) => getFieldValue(siblingName))
            .reduce((accumulator, sibling) => accumulator || (sibling && !isEmpty(sibling)), false)
        : false;

      if (hasValue || isNotMatchingTrigger || atLeastOneSiblingWithValue) {
        return Promise.resolve();
      }
      return Promise.reject(<FormattedMessage {...messages.required} />);
    },
  });

export const buildNonPreferredAirlinePreference = (preference, airlines, locationsDict, ratings) => {
  const {config, id, name, valid} = preference;
  const {airlinePreferenceRatings, locationIds, airlineIds} = config;

  const ratingList =
    !isEmpty(airlinePreferenceRatings) &&
    ratings
      .filter((rating) => airlinePreferenceRatings.includes(rating.value))
      .map((rating) => ({id: rating.value, name: rating.label}));

  return {
    id,
    name,
    valid,
    locationIds,
    airlinePreferenceRatings,
    airlineIds,
    airlinePreferenceRatingTags: ratingList,
    locations: !isEmpty(locationIds) ? formatLocations(locationIds, locationsDict) : undefined,
    airlines: airlines?.map(({id, iataCode, name}) => ({id, name: `${iataCode} - ${name}`})),
  };
};

export const buildAirlineRatingChangePreference = (preference, airlines) => {
  const {config, id, name, valid} = preference;
  const {airlineIds} = config;

  const airlineTags = airlines?.length
    ? airlines?.map(({id, name}) => ({id, name: name}))
    : [{id: 'allAirlines', name: localization.formatMessage(airlineRatingChangesMessages.allAirlines)}];
  return {
    id,
    name,
    valid,
    airlineIds,
    airlines: airlineTags,
  };
};

export const locationsKeyMap = {
  ALERT_NOTIFICATION: 'locations',
};

export const categoriesKeyMap = {
  ALERT_NOTIFICATION: 'categories',
};

export const buildMappedPreference = (preference, categoriesDict, locationsDict) => {
  const {config, id, name, valid, type} = preference;

  const isAlert = type === NOTIFICATION_TYPES.ALERT_NOTIFICATION;

  const formatPreferenceLocations = (locationKey) => {
    return formatLocations(config[locationKey], locationsDict);
  };

  const formatPreferenceCategories = (categoriesKey) => {
    return formatCategories(config[categoriesKey], categoriesDict);
  };

  const formatSitesAffected = (sites) => {
    return sites?.map((site) => ({
      ...site,
      value: site.id,
      label: site.name,
    }));
  };

  const formatDuration = (duration) => {
    return {
      startAt: moment(duration.startAt),
      endAt: moment(duration.endAt),
    };
  };

  const formatPeopleAffected = (peopleAffected) => {
    if (peopleAffected) {
      const formattedPersonLocationTypes = peopleAffected?.personLocationTypes
        ?.map((type) => inversePersonLocationTypeMap[type])
        .filter(Boolean);
      return {
        ...peopleAffected,
        personLocationTypes: [
          ...(peopleAffected?.mobileLocationSubtypes ?? []),
          ...(peopleAffected?.tripSubtypes ?? []),
          ...(formattedPersonLocationTypes ?? []),
        ],
      };
    }
  };

  const locationKey = locationsKeyMap?.[type];
  const categoriesKey = categoriesKeyMap?.[type];

  const baseConfig = {
    id,
    name,
    valid,
    [locationKey]: formatPreferenceLocations(locationKey),
    [categoriesKey]: formatPreferenceCategories(categoriesKey),
    alwaysSendUniqueNotification: config?.alwaysSendUniqueNotification,
    modalities: formatModalities(config.modalities),
    sitesAffected: config?.sitesAffected
      ? {
          ...config.sitesAffected,
          siteIds: formatSitesAffected(preference?.sites),
        }
      : undefined,
  };

  if (isAlert) {
    baseConfig.severities = formatSeverities(config.severities);
    baseConfig.peopleAffected = formatPeopleAffected(config.peopleAffected);

    baseConfig.organizations = config.organizations;

    if (config.notificationProviders?.length) {
      baseConfig.providers = config.notificationProviders;
    }
  }

  if (config.duration) {
    baseConfig.duration = formatDuration(config.duration);
  }

  return baseConfig;
};

/**
 * Checks if any organization has the required permissions
 * @param {Object} rolledUpOrgPermissions - rolled up user organization permissions
 * @param {Array} requiredPermissions - list of required permission IDs
 * @returns {Array}  matchingOrgIds array
 */
export const getOrganizationsWithMatchingPermissions = (rolledUpOrgPermissions, requiredPermissions) => {
  let matchingOrgIds = [];
  if (rolledUpOrgPermissions?.list) {
    for (const rollUp of rolledUpOrgPermissions.list) {
      if (rollUp.id !== DEFAULT_PARENT_ID) {
        let foundMatch = false;
        for (const assignment of rollUp?.roleAssignments || []) {
          for (const permission of assignment.organizationRole.enabledPermissions || []) {
            if (requiredPermissions?.includes(permission.id)) {
              foundMatch = true;
              break;
            }
          }
          // no need to continue looping through role assignments/permissions in the same org if a match is found
          if (foundMatch) break;
        }
        if (foundMatch) {
          matchingOrgIds.push(rollUp.id);
        }
      }
    }
  }

  return matchingOrgIds;
};
